import { AfterContentInit, Component, Input, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { of } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';

import { FontEnum, SignatureFormat, Signer, Document, SlimDocument, User, UserSignatureInput, SignaturePadEnum } from '@app/models';
import { DocumentService, ErrorHandlerService, NotyService, UserService } from '@app/services';
import { SignaturePadComponent } from '@app/shared/signature-pad/signature-pad.component';
import { TranslateService } from '@ngx-translate/core';
import { InitialsNamePipe } from '@app/shared/pipes/initials-name.pipe';
import { SignatureNamePipe } from '@app/shared/pipes/signature-name.pipe';

export type ModalResult = void;
export interface ModalPublicProperties {
  user: User;
  document: SlimDocument | Document;
  signers: Signer[];
  disableSignature?: boolean;
  disableInitials?: boolean;
}

@Component({
  selector: 'app-choose-signature-modal',
  templateUrl: './choose-signature-modal.component.html',
  styleUrls: ['./choose-signature-modal.component.scss'],
  standalone: false
})
export class ChooseSignatureModalComponent implements AfterContentInit, ModalPublicProperties {
  @Input() user: User;
  @Input() signers: Signer[];
  @Input() document: SlimDocument | Document;

  @Input() disableSignature: boolean;
  @Input() disableInitials: boolean;
  signatureNamePipe = new SignatureNamePipe();
  initialsPipe = new InitialsNamePipe();
  canSubmitForm = true;
  form!: UntypedFormGroup;
  SignatureFormat = SignatureFormat;
  fonts = {
    [FontEnum.Autograf]: 'font_enum_autograf',
    [FontEnum.Fathur]: 'font_enum_farthur',
    [FontEnum.Robertson]: 'font_enum_robertson'
  };
  isLoading = false;
  SignaturePadEnum = SignaturePadEnum;
  signaturePadStep = SignaturePadEnum.Signature;
  isMobile = false;

  @ViewChild('signaturePad', { static: false }) private signaturePad: SignaturePadComponent;

  constructor(
    public modal: NgbActiveModal,
    private formBuilder: UntypedFormBuilder,
    private errorHandlerService: ErrorHandlerService,
    private notyService: NotyService,
    private userService: UserService,
    private translateService: TranslateService,
    private documentService: DocumentService
  ) {
    this.form = this.formBuilder.group({
      signature: this.formBuilder.group({
        font: FontEnum.Autograf,
        format: SignatureFormat.Handwriting,
        text: '',
        file_image: null,
        file_draw: null
      }),
      initials: this.formBuilder.group({
        font: FontEnum.Autograf,
        format: SignatureFormat.Handwriting,
        text: '',
        file_image: null,
        file_draw: null
      })
    });
  }

  ngAfterContentInit() {
    this.isMobile = window.innerWidth <= 992;

    const font = this.user?.settings?.font || FontEnum.Autograf;
    const mappedFormat = this.documentService.signatureAppearanceToFormats[this.document?.configs?.signature_appearance];
    const format = mappedFormat || this.user?.settings?.format || SignatureFormat.Handwriting;
    const signatureText = this.user?.settings?.signature_text || this.signatureNamePipe.transform(this.user.name);
    const initialsText = this.user?.settings?.initials_text || this.initialsPipe.transform(this.user.name);

    this.form.patchValue({
      signature: { font, format, text: signatureText },
      initials: { font, format, text: initialsText }
    });
  }

  update() {
    if (!this.canSubmitForm) {
      return;
    }

    this.form.markAllAsTouched();

    const signatureData = this.form.get('signature')?.value;
    const initialsData = this.form.get('initials')?.value;

    const signatureFormat: SignatureFormat = signatureData.format;
    const initialsFormat: SignatureFormat = initialsData.format;

    const isSignatureEmpty =
      (signatureFormat === SignatureFormat.Draw && (this.signaturePad.isEmptySignature() || this.signaturePad.isMinSize(SignaturePadEnum.Signature))) ||
      (signatureFormat === SignatureFormat.Image && !this.signaturePad.selectedSignatureImage && !this.signaturePad.selectedSignatureImageUrl) ||
      (signatureFormat === SignatureFormat.Handwriting && !signatureData.text?.trim());

    const isInitialsEmpty =
      (initialsFormat === SignatureFormat.Draw && (this.signaturePad.isEmptyInitials() || this.signaturePad.isMinSize(SignaturePadEnum.Initials))) ||
      (initialsFormat === SignatureFormat.Image && !this.signaturePad.selectedInitialsImage && !this.signaturePad.selectedInitialsImageUrl) ||
      (initialsFormat === SignatureFormat.Handwriting && !initialsData.text?.trim());

    if (isSignatureEmpty && isInitialsEmpty) {
      this.notyService.error(this.translateService.instant('notyService.cannotSendEmptySignature'));
      return;
    }

    const signatureParams: { user: UserSignatureInput } = { user: { ...signatureData } };
    const initialsParams: { user: UserSignatureInput } = { user: { ...initialsData } };

    if (signatureFormat === SignatureFormat.Draw) {
      signatureParams.user.file_draw = this.signaturePad.toImageFile(SignaturePadEnum.Signature);
    } else if (signatureFormat === SignatureFormat.Image) {
      signatureParams.user.file_image = this.signaturePad.toImageFile(SignaturePadEnum.Signature);
    }

    if (initialsFormat === SignatureFormat.Draw) {
      initialsParams.user.file_draw = this.signaturePad.toImageFile(SignaturePadEnum.Initials);
    } else if (initialsFormat === SignatureFormat.Image) {
      initialsParams.user.file_image = this.signaturePad.toImageFile(SignaturePadEnum.Initials);
    }

    [signatureParams, initialsParams].forEach(param => {
      if (!param.user.file_draw) delete param.user.file_draw;
      if (!param.user.file_image) delete param.user.file_image;
    });

    this.isLoading = true;

    const signatureRequest$ = isSignatureEmpty ? of(null) : this.userService.updateCurrentUserSignature(signatureParams);
    const initialsRequest$ = isInitialsEmpty ? of(null) : this.userService.updateCurrentUserInitials(initialsParams);

    signatureRequest$
      .pipe(
        switchMap(() => initialsRequest$),
        finalize(() => {
          this.isLoading = false;
          this.signaturePad.clear();
        })
      )
      .subscribe({
        next: () => this.modal.close(),
        error: err => this.errorHandlerService.handle(err)
      });
  }
}
